import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { Lightbulb, Zap, HardHat, Gauge, ArrowRight, CheckCircle } from 'lucide-react';
import LogoCarousel from '../components/LogoCarousel';
import SEO from '../components/SEO';

const Home = () => {
  const [api, setApi] = useState();

  // Homepage-specific structured data schemas
  const homeServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Multi Utility Connections and Metering",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Green Frog Utilities"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Multi Utility Connection and Metering Services",
      "itemListElement": [
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Multi Utility Connections"}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Gas Connections"}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Water Connections"}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Electrical Connections"}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Gas Meter Installation"}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "AMR Metering"}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Utility Infrastructure Design"}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Utility Construction"}}
      ]
    }
  };

  const homeFaqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is AMR gas metering?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "AMR (Automated Meter Reading) gas metering systems automatically collect gas consumption data and transmit it remotely to utility companies, eliminating the need for manual meter readings. This technology provides accurate billing, real-time monitoring, and helps businesses track their gas usage efficiently."
        }
      },
      {
        "@type": "Question",
        "name": "How long does commercial gas meter installation take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Commercial gas meter installation typically takes 1-5 days depending on the complexity of the installation, site conditions, and meter capacity required. Our experienced team handles all aspects from design through to commissioning."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide gas meter installation near me?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, Green Frog Utilities provides commercial and industrial gas meter installation services across the entire United Kingdom. We are GIRS accredited and Gas Safe registered, ensuring compliance with all UK gas meter installation standards."
        }
      },
      {
        "@type": "Question",
        "name": "What types of gas meters do you install?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We install all types of commercial and industrial gas meters including rotary gas meters, turbine gas meters, diaphragm gas meters, and ultrasonic gas flow meters. We also provide gas meter pressure regulating systems and capacity sizing services."
        }
      }
    ]
  };

  const homeBreadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://greenfrogutilities.co.uk/"
      }
    ]
  };

  // Removed auto-scroll - carousel now only moves on manual interaction

  const heroSlides = [
    {
      image: 'https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/chzo27xv_006.jpg',
      title: 'Multi Utility Connections UK',
      subtitle: 'Take the stress out of getting connected with our end-to-end, co-ordinated and integrated multi-utility solutions. Gas, water and electrical connections from one accredited contractor.',
      buttonText: 'Connections',
      buttonLink: '/services/connections',
      external: false
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/hq7bk3hn_Screenshot%202026-03-19%20at%2010.59.56.png',
      title: 'Gas, Water and Electrical Connections',
      subtitle: 'Get connected without the hassle. Our highly skilled and experienced teams take care of the whole process from start to finish. Gas, water and electrical connections from one accredited contractor.',
      buttonText: 'Construction',
      buttonLink: '/services/construction',
      external: false
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/5n8b3byx_DSCN2684.JPG',
      title: 'Commercial Metering and AMR Systems',
      subtitle: 'Take charge of your consumption with AMR metering and remote monitoring for gas, water and electricity. Installed and managed by Green Frog Asset Management.',
      buttonText: 'Metering',
      buttonLink: 'https://greenfrogassetmanagement.co.uk',
      external: true,
      zoomLevel: 1.3
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/eh2vd4sl_Screenshot%202026-03-19%20at%2011.06.06.png',
      title: 'Utility Infrastructure Design and Construction',
      subtitle: 'Find your utilities solution with our design team - for all of your infrastructural requirements. Gas, water and electrical connections from one accredited contractor.',
      buttonText: 'Design',
      buttonLink: '/services/design',
      external: false
    }
  ];

  const services = [
    {
      icon: Lightbulb,
      title: 'Design',
      description: 'Our solutions are driven by experienced personnel, adopting the latest design and engineering practices.',
      link: '/services/design',
      image: 'https://greenfrogutilities.co.uk/wp-content/uploads/2020/05/design-icon-150x150.png'
    },
    {
      icon: Zap,
      title: 'Gas Connections',
      description: 'Our experienced project managers will ensure your connection will be operational on-time and on-budget.',
      link: '/services/connections',
      image: 'https://greenfrogutilities.co.uk/wp-content/uploads/2020/05/connection-icon-150x150.png'
    },
    {
      icon: HardHat,
      title: 'Construction',
      description: 'Our team has the knowledge and experience to handle all aspects of complex utilities construction.',
      link: '/services/construction',
      image: 'https://greenfrogutilities.co.uk/wp-content/uploads/2020/05/construction-icon-150x150.png'
    },
    {
      icon: Gauge,
      title: 'Metering',
      description: 'We offer full MAM and MAP services and manufacture a range of meters so we offer shorter lead times.',
      link: 'https://greenfrogassetmanagement.co.uk',
      external: true,
      image: 'https://greenfrogutilities.co.uk/wp-content/uploads/2020/05/metering-icon-150x150.png'
    }
  ];

  const stats = [
    { number: '40+', label: 'Years Experience' },
    { number: '250+', label: 'Projects Completed' }
  ];

  const accreditations = [
    'https://greenfrogutilities.co.uk/wp-content/uploads/2020/05/NICEIC-Logo-web.png',
    'https://greenfrogutilities.co.uk/wp-content/uploads/2020/05/GIRS-Logo-web.png'
  ];

  return (
    <div>
      <SEO 
        title="Multi Utility Connections & Metering UK | Green Frog Utilities"
        description="Multi utility connections and metering across the UK. Gas, water and electrical connections from one accredited contractor. WIRS, NERS and GIRS accredited. Design, construction and metering for developers and commercial sites."
        keywords="multi utility connections UK, gas water electrical connections, WIRS accredited, NERS accredited, GIRS accredited, utility connections UK, commercial metering"
        canonical="/"
        jsonLd={[homeServiceSchema, homeFaqSchema, homeBreadcrumbSchema]}
      />
      {/* Hero Carousel */}
      <section className="relative">
        <Carousel 
          className="w-full"
          setApi={setApi}
          opts={{ loop: true }}
        >
          <CarouselContent>
            {heroSlides.map((slide, index) => (
              <CarouselItem key={index}>
                <div className="relative h-[600px] lg:h-[700px] overflow-hidden">
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ 
                      backgroundImage: `url(${slide.image})`,
                      filter: 'brightness(1.2) contrast(1.05) saturate(0.9)',
                      transform: slide.zoomLevel ? `scale(${slide.zoomLevel})` : 'scale(1)'
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30" />
                  <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
                    <div className="max-w-2xl text-white">
                      <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                        {slide.title}
                      </h1>
                      <p className="text-lg md:text-xl mb-8 text-gray-200">
                        {slide.subtitle}
                      </p>
                      <div className="flex flex-wrap gap-4">
                        {slide.external ? (
                          <a href={slide.buttonLink} target="_blank" rel="noopener noreferrer">
                            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                              {slide.buttonText}
                            </Button>
                          </a>
                        ) : (
                          <Link to={slide.buttonLink}>
                            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                              {slide.buttonText}
                            </Button>
                          </Link>
                        )}
                        <Link to="/contact">
                          <Button size="lg" variant="outline" className="bg-white/10 border-white text-white hover:bg-white hover:text-emerald-600 backdrop-blur-sm">
                            Get a Quote
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-4 h-12 w-12 bg-emerald-600/80 hover:bg-emerald-600 border-2 border-white text-white" />
          <CarouselNext className="right-4 h-12 w-12 bg-emerald-600/80 hover:bg-emerald-600 border-2 border-white text-white" />
        </Carousel>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive utilities solutions backed by extensive experience and industry-leading expertise
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              const content = (
                <Card className="h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-none group">
                  <CardContent className="p-8 text-center flex flex-col items-center">
                    <div className="mb-6 p-4 bg-emerald-50 rounded-full group-hover:bg-emerald-100 transition-colors">
                      <Icon className="h-12 w-12 text-emerald-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-6 flex-grow">
                      {service.description}
                    </p>
                    <span className="inline-flex items-center text-emerald-600 font-semibold group-hover:gap-2 transition-all">
                      Find out more
                      <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </CardContent>
                </Card>
              );

              return service.external ? (
                <a key={index} href={service.link} target="_blank" rel="noopener noreferrer">
                  {content}
                </a>
              ) : (
                <Link key={index} to={service.link}>
                  {content}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Client Logos Carousel */}
      <LogoCarousel />

      {/* Stats Section */}
      <section className="py-20 bg-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Bespoke, integrated utilities solutions
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl md:text-6xl font-bold mb-2">
                  {stat.number}
                </div>
                <div className="text-xl text-emerald-100">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Get connected without the hassle
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Our highly skilled and experienced teams take care of the whole process from start to finish. We provide end-to-end utilities solutions that are coordinated and integrated.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Experienced personnel with latest design and engineering practices</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">On-time and on-budget project delivery</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Comprehensive knowledge for complex utilities construction</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Full MAM and MAP services with shorter lead times</span>
                </li>
              </ul>
              <div className="mt-8">
                <Link to="/about">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    Learn More About Us
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/cf2oywsd_147.%20Photo%204%20%2025.07.18%20%281%29.jpg"
                alt="Utilities Infrastructure"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Accreditations */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Fully Accredited & Certified
            </h2>
            <p className="text-lg text-gray-600">
              We maintain the highest industry compliance standards with comprehensive certification
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {/* 1. ISO - ISOQAR & UKAS */}
            <div className="bg-white p-3 rounded-lg shadow-md hover:shadow-lg transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/or7bnh4g_01-ISOQAR-UKAS.png"
                alt="ISOQAR Registered and UKAS Management Systems"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            {/* 2. Construction Line Gold */}
            <div className="bg-white p-3 rounded-lg shadow-md hover:shadow-lg transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/mmnow9bf_02-ConstructionLine-Gold.jpg"
                alt="Constructionline Gold member"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            {/* 3. Achilles UVDB Silver Plus */}
            <div className="bg-white p-3 rounded-lg shadow-md hover:shadow-lg transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/mlz8i6t2_03-Achilles-UVDB-SilverPlus.jpg"
                alt="Achilles UVDB Silver Plus"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            {/* 4. GIRS */}
            <div className="bg-white p-3 rounded-lg shadow-md hover:shadow-lg transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/7dxbfb2l_GIRS-accredited.png"
                alt="GIRS accredited gas contractor, LRQA"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            {/* 5. NERS */}
            <div className="bg-white p-3 rounded-lg shadow-md hover:shadow-lg transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/rqw3vpgj_NERS-accredited.png"
                alt="NERS accredited electrical contractor, LRQA"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            {/* 6. WIRS */}
            <div className="bg-white p-3 rounded-lg shadow-md hover:shadow-lg transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/wljohupy_WIRS-accredited.png"
                alt="WIRS accredited water contractor, LRQA"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            {/* 7. Gas Safe */}
            <div className="bg-white p-3 rounded-lg shadow-md hover:shadow-lg transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/h7w5jyk6_05-GasSafe.jpg"
                alt="Gas Safe registered"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            {/* 6. IGEM */}
            <div className="bg-white p-3 rounded-lg shadow-md hover:shadow-lg transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/s4lr6tj8_06-IGEM.png"
                alt="IGEM member"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            {/* 7. Cyber Essentials - existing embed */}
            <div className="bg-white p-2 rounded-lg shadow-md hover:shadow-lg transition-shadow flex items-center justify-center min-h-[120px]">
              <iframe 
                src="https://registry.blockmarktech.com/certificates/c871dedf-9ab4-47d8-a7de-cd2f2de05cbb/widget/?tooltip_position=bottom&theme=transparent" 
                style={{ border: 'none', height: '132px', width: '132px' }} 
                loading="lazy"
                title="Cyber Essentials Certified"
              />
            </div>
            {/* 8. CoMCoP Accredited */}
            <div className="bg-white p-3 rounded-lg shadow-md hover:shadow-lg transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="/images/comcop-accredited.png"
                alt="CoMCoP Accredited"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            {/* 9. SSIP */}
            <div className="bg-white p-3 rounded-lg shadow-md hover:shadow-lg transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/6v1vokrm_09-SSIP.jpg"
                alt="SSIP assessed"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            {/* 10. SafeContractor */}
            <div className="bg-white p-3 rounded-lg shadow-md hover:shadow-lg transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/0obf2zin_10-SafeContractor.png"
                alt="SafeContractor approved"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to get started?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Contact us today for a free consultation and quote for your utilities project
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100">
              Get a Quote Today
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
